import { ISounds } from './interfaces';
import sounds from './sounds/files.json';

const AUDIO_BUFFER: Record<string, AudioBuffer> = {};

let audioContextInstance: AudioContext;
let gainNode: GainNode;
const AUDIO_VOLUME = 3; // Float number range from -3.4028235E38 to 3.4028235E38

// This API has some inconsistencies between the browsers. See more at:
// https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/AudioContext
const AudioContext = window.AudioContext || window.webkitAudioContext;

if (!AudioContext) {
    console.warn('Web Audio API not supported in this browser.');
} else {
    audioContextInstance = new AudioContext();

    try {
        gainNode = audioContextInstance.createGain();
        gainNode.gain.value = AUDIO_VOLUME;
    } catch (err) {
        console.warn('Can not change the volume');
    }
}

const PLAYLIST_DELAY = 0.5;

enum AudioState {
    SUSPENDED = 'suspended',
    RUNNING = 'running',
}

export class AudioService {
    public sounds: ISounds = sounds;

    private filesPathUrl = '/sounds';

    private muted = true;

    private fileExtension = '.mp3';

    private playing = false;

    static decodeAudio(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
        return new Promise((resolve, reject) => {
            // The newest decodeAudioData API returns promise. But older API is callback based.
            // https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/decodeAudioData
            audioContextInstance.decodeAudioData(
                arrayBuffer,
                (audioBuffer) => resolve(audioBuffer),
                reject,
            );
        });
    }

    private getAudioBuffer(fileName: string): Promise<AudioBuffer> {
        if (AUDIO_BUFFER[fileName]) {
            return Promise.resolve(AUDIO_BUFFER[fileName]);
        }

        return fetch(`${this.filesPathUrl}/${fileName}${this.fileExtension}`)
            .then((response) => response.arrayBuffer())
            .then(AudioService.decodeAudio)
            .then((audioBuffer) => {
                AUDIO_BUFFER[fileName] = audioBuffer;

                return audioBuffer;
            });
    }

    public play(fileName: string, callback: () => void = null, startProps: number[] = []): void {
        if (
            audioContextInstance?.state === AudioState.SUSPENDED ||
            this.muted ||
            !fileName ||
            this.playing
        ) {
            return;
        }

        this.playing = true;
        this.getAudioBuffer(fileName as string).then((audioBuffer) => {
            try {
                const source = audioContextInstance.createBufferSource();
                source.buffer = audioBuffer;

                if (gainNode) {
                    source.connect(gainNode).connect(audioContextInstance.destination);
                } else {
                    source.connect(audioContextInstance.destination);
                }

                source.onended = () => {
                    this.playing = false;
                    if (callback) {
                        callback();
                    }
                };
                source.start(...startProps);
            } catch (e) {
                this.playing = false;
                console.error(e);
            }
        });
    }

    runPlaylist(playlist: string[]): void {
        let index = 0;

        const onEnd = (): void => {
            index++;

            window.setTimeout(() => {
                this.play(playlist[index], index === playlist.length - 1 ? null : onEnd);
            }, PLAYLIST_DELAY * 1000);
        };

        this.play(playlist[0], onEnd);
    }

    public mute(): void {
        if (audioContextInstance?.state === AudioState.RUNNING) {
            audioContextInstance.suspend();
        }

        this.muted = true;
    }

    public unMute(): void {
        if (audioContextInstance?.state === AudioState.SUSPENDED) {
            audioContextInstance.resume();
        }

        this.muted = false;
    }

    public muteWorker(isMuted: boolean): void {
        if (isMuted) {
            this.mute();
        } else {
            this.unMute();
        }
    }

    public refresh(): void {
        audioContextInstance?.close();
        audioContextInstance = new AudioContext();
        try {
            gainNode = audioContextInstance.createGain();
            gainNode.gain.value = AUDIO_VOLUME;
        } catch (err) {
            console.warn('Can not change the volume');
        }

        this.playing = false;
    }

    get isMuted(): boolean {
        return this.muted;
    }

    get isPlaying() {
        return this.playing;
    }
}
